import React, { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { Contact, Dropdown, formatDate, Subheader, Text } from './FieldComponents';
import Alert from './Alert';

export const LeadIntake = ({ leadData, fetchCustomFields, contacts, fetchLead }) => {
    const [editing, setEditing] = useState({});
    const [fieldValues, setFieldValues] = useState({});
    const [marketingSources, setMarketingSources] = useState([]);
    const [message, setMessage] = useState(null);
    const [users, setUsers] = useState([]);
    const caseLikelihood = ["No Case", "Unlikely Case", "Possible Case", "Likely Case", "Very Likely Case"];

    const handleEdit = (field) => {
        setEditing({...editing, [field]: true});
        setFieldValues({...fieldValues, [field]: leadData[field] || ''});
    };

    const fetchUsers = async () => {
        try {
            const response = await fetch('https://api.casedb.co/user.php?users=true');
            const result = await response.json();
            if (response.ok) {
                setUsers(result.users);
            } else {
                console.error('Failed to fetch users:', result.message);
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setMessage({ type: 'error', text: 'Failed to fetch users' });
        }
    };

    const handleSave = async () => {
        try {
            const response = await fetch('https://api.casedb.co/leads.php', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    lead_id: leadData.id,
                    ...fieldValues
                }),
            });

            const result = await response.json();
            
            if (result.success) {
                setEditing({});
                fetchCustomFields();
                if (fetchLead) fetchLead();
                setMessage({ type: 'success', text: 'Lead updated successfully' });
            } else {
                console.error('Failed to update lead:', result.message);
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            console.error('Error updating lead:', error);
            setMessage({ type: 'error', text: 'Failed to update lead' });
        }
    };

    const renderField = (label, field, type = 'text') => {
        const value = leadData[field];
        
        if (editing[field]) {
            if (type === 'date') {
                return (
                        <input 
                            type="date" 
                            value={fieldValues[field]} 
                            onChange={(e) => setFieldValues({...fieldValues, [field]: e.target.value})}
                        />
                );
            } else if (type === 'textarea') {
                return (
                        <textarea
                            value={fieldValues[field]}
                            onChange={(e) => setFieldValues({...fieldValues, [field]: e.target.value})}
                        />
                );
            } else {
                return (
                        <input 
                            type={type} 
                            value={fieldValues[field]} 
                            onChange={(e) => setFieldValues({...fieldValues, [field]: e.target.value})}
                        />
                );
            }
        }
    };

    useEffect(() => {
        if (!leadData.marketing_sources) {
            return;
        }
        
        const sorted = [...leadData.marketing_sources].sort((a, b) => {
            if (a.marketing_type === b.marketing_type) {
                return a.order_id - b.order_id;
            }
            return a.marketing_type - b.marketing_type;
        });
        setMarketingSources(sorted);
        fetchUsers();
        console.log('Lead data updated:', leadData);
    }, [leadData]);

    return (
        <div className="lead-intake case-section">
            <div className='toc-header'>
                <h3>Intake Information</h3>
                <button className='action' onClick={handleSave}>
                    Save
                </button>
            </div>
            <div className='sub-section'>
                <div className='form-group nm'>
                    <label className='subtext'>Intake By</label>
                    <Contact label='Intake By' selectedContact={leadData?.intake_user?.[0]?.id} users={leadData?.intake_user} disabled />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Intake Date</label>
                    <input
                        type="text" 
                        value={formatDate(leadData?.created_at)[5]} 
                        disabled
                    />
                </div>
            </div>
            {leadData?.id && (
                <>
                    <div className='sub-section'>
                        <div className='form-group nm'>
                            <label className='subtext'>Marketing Source</label>
                            <Dropdown
                                value={fieldValues.marketing_source ?? leadData?.marketing_source}
                                marketing_list={true}
                                lead_filter={[1, 3]}
                                onChange={(value) => setFieldValues({...fieldValues, marketing_source: value})}
                            />
                        </div>
                        <div className='form-group nm'>
                            <label className='subtext'>Marketing Lead</label>
                            <Dropdown
                                value={fieldValues.marketing_lead ?? leadData?.marketing_lead}
                                marketing_list={true}
                                lead_filter={2}
                                onChange={(value) => setFieldValues({...fieldValues, marketing_lead: value})}
                            />
                        </div>
                        <div className='form-group nm'>
                            <label className='subtext'>Marketing Notes</label>
                            <Text label='Marketing Notes' value={fieldValues.marketing_notes ?? leadData?.marketing_notes} onChange={(val) => setFieldValues({...fieldValues, marketing_notes: val})} placeholder="Marketing notes..." />
                        </div>
                        <div className='form-group nm'>
                            <label className='subtext'>Referred By</label>
                            <Contact key={leadData?.referral_contact} selectedContact={leadData?.referral_contact} setSelectedContact={(contact) => setFieldValues({...fieldValues, referral_contact: Number(contact.id)})} />
                        </div>
                        <div className='form-group nm'>
                            <label className='subtext'>DB Origination</label>
                            <Contact key={leadData?.db_origination} selectedContact={leadData?.db_origination} setSelectedContact={(contact) => setFieldValues({...fieldValues, db_origination: Number(contact.id)})} />
                        </div>
                        <div className='form-group nm'>
                            <label className='subtext'>Summary</label>
                            <Text label='Summary' value={fieldValues.summary ?? leadData?.summary} onChange={(val) => setFieldValues({...fieldValues, summary: val})} placeholder="Summary..." type="textarea" />
                        </div>
                    </div>
                    <Subheader title="Office Details" />
                    <div className='sub-section' style={{ paddingTop: '20px' }}>
                        <div className='form-group nm'>
                            <label className='subtext'>Severity Level</label>
                            <select className='default-select' value={fieldValues.case_likelihood ?? leadData?.case_likelihood} onChange={(e) => setFieldValues({...fieldValues, case_likelihood: e.target.value})}>
                                <option value="">Select Severity Level</option>
                                {caseLikelihood.map((level, index) => (
                                    <option key={index} value={index + 1}>
                                        {level}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='form-group nm'>
                            <label className='subtext'>Office</label>
                            <select className='default-select' value={fieldValues.office ?? leadData?.office} onChange={(e) => setFieldValues({...fieldValues, office: e.target.value})}>
                                <option value="">Select Office</option>
                                {leadData?.offices && leadData?.offices?.map((office, index) => (
                                    <option key={index} value={office.id}>
                                        {office.city}, {office.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='form-group nm'>
                            <label className='subtext'>Assigned to Intake</label>
                            <Contact key={users} selectedContact={leadData?.assigned_to} users={users} setSelectedContact={(contact) => setFieldValues({...fieldValues, assigned_to: Number(contact.id)})} />
                        </div>
                        <div className='form-group nm'>
                            <label className='subtext'>Assigned to Attorney</label>
                            <Contact key={users} selectedContact={leadData?.assigned_to_attorney} users={users} setSelectedContact={(contact) => setFieldValues({...fieldValues, assigned_to_attorney: Number(contact.id)})} />
                        </div>
                    </div>
                </>
            )}
            {message && <Alert type={message.type} message={message.text} onClose={() => setMessage(null)} />}
        </div>
    );
};